import { useSyncExternalStore } from 'react';

/**
 * Canonical home for the app's dark-launch feature flags.
 *
 * Flags default ON (the feature is being launched) but can be overridden at
 * runtime per-browser via a localStorage key — useful for a safe rollback or to
 * A/B a single tester without a redeploy. The reader is window-guarded so it is
 * safe in any (SSR / worker / test) context.
 *
 * Override values (localStorage):
 *   "0" / "false" / "off" / "no"  → force the flag OFF
 *   "1" / "true"  / "on"  / "yes" → force the flag ON
 *   (absent / anything else)      → fall back to the compiled default
 *
 * Keep this module tiny and dependency-free (only React for the optional hook).
 */

export interface FeatureFlags {
  /** Unified Activity Center + slim app topbar dark-launch. */
  activityCenter: boolean;
}

/** Compiled-in defaults. The Activity Center launches ON. */
export const FLAG_DEFAULTS: Readonly<FeatureFlags> = Object.freeze({
  activityCenter: true,
});

/** localStorage keys backing each runtime override. */
export const FLAG_STORAGE_KEYS: Readonly<Record<keyof FeatureFlags, string>> = Object.freeze({
  activityCenter: 'pcp.flags.activityCenter',
});

const TRUTHY = new Set(['1', 'true', 'on', 'yes']);
const FALSY = new Set(['0', 'false', 'off', 'no']);

/** Read a single override safely; null when unset / not parseable / no window. */
function readOverride(key: string): boolean | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null; // storage disabled (private mode, etc.)
  }
  if (raw == null) return null;
  const v = raw.trim().toLowerCase();
  if (TRUTHY.has(v)) return true;
  if (FALSY.has(v)) return false;
  return null;
}

/** Resolve one flag = its override (if any) else the compiled default. */
export function readFlag(name: keyof FeatureFlags): boolean {
  const override = readOverride(FLAG_STORAGE_KEYS[name]);
  return override ?? FLAG_DEFAULTS[name];
}

/** Snapshot of all resolved flags (override ∪ defaults). */
export function getFeatureFlags(): FeatureFlags {
  return { activityCenter: readFlag('activityCenter') };
}

/** Is the Unified Activity Center (+ slim topbar) enabled for this browser? */
export function isActivityCenterEnabled(): boolean {
  return readFlag('activityCenter');
}

// ── Optional reactive hook ────────────────────────────────────────────────
// Flags are effectively static for a session (an override is set out-of-band),
// but a `storage` event from another tab should re-render so all tabs agree.

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('storage', onChange);
  return () => window.removeEventListener('storage', onChange);
}

/** Reactive reader for the Activity Center flag (re-renders on cross-tab change). */
export function useActivityCenterFlag(): boolean {
  return useSyncExternalStore(subscribe, isActivityCenterEnabled, () => FLAG_DEFAULTS.activityCenter);
}
